import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { TrendingUp, TrendingDown, Wallet, Clock, AlertTriangle, Info } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import DashboardArang from "@/pages/DashboardArang";

const Stat = ({ label, value, icon: Icon, tone, testid }) => (
  <div data-testid={testid} className="card-flat p-4 md:p-5">
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`h-9 w-9 flex items-center justify-center rounded-sm ${tone}`}>
        <Icon size={16} className="text-white"/>
      </div>
    </div>
    <div className="mt-3 md:mt-4 font-mono text-lg md:text-2xl tabular-nums text-slate-900">{rupiah(value)}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [trend, setTrend] = useState([]);
  const [reminder, setReminder] = useState({ reminders: [] });

  const isArang = user?.workspace_type === "arang";

  useEffect(() => {
    if (isArang) return; // Arang dashboard handled by DashboardArang
    api.get("/dashboard").then(r => setD(r.data));
    api.get("/dashboard/trend").then(r => setTrend(r.data));
    api.get("/dashboard/pajak-reminder").then(r => setReminder(r.data));
  }, [isArang]);

  if (isArang) return <DashboardArang/>;

  const chartData = trend.map(t => ({
    name: t.bulan.split(" ")[0].slice(0,3),
    pemasukan: t.pemasukan,
    pengeluaran: t.pengeluaran,
    laba: t.pemasukan - t.pengeluaran,
  }));

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Ringkasan keuangan Anda saat ini."/>
      <div className="p-4 md:p-8 space-y-6">
        {reminder.reminders?.length > 0 && (
          <div className="space-y-3" data-testid="pajak-reminders">
            {reminder.reminders.map((r, i) => (
              <div key={i} className={`card-flat p-4 md:p-5 flex gap-4 border-l-4 ${r.level==="warning"?"border-l-amber-500":"border-l-blue-500"}`}>
                {r.level==="warning" ? <AlertTriangle className="text-amber-500 shrink-0" size={20}/> : <Info className="brand-text shrink-0" size={20}/>}
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 text-sm md:text-base">{r.title}</div>
                  <div className="text-xs md:text-sm text-slate-600 mt-1">{r.message}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm">
                    <span className="text-slate-500">Estimasi PPh 0,5%:</span>
                    <span className="font-mono font-semibold brand-text">{rupiah(r.amount)}</span>
                    <span className="text-slate-500">Jatuh tempo: <b className="text-slate-900">{r.due_date}</b></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          <Stat testid="stat-pemasukan" label="Total Pendapatan" value={d?.total_pemasukan||0} icon={TrendingUp} tone="bg-emerald-600"/>
          <Stat testid="stat-pengeluaran" label="Total Pengeluaran" value={d?.total_pengeluaran||0} icon={TrendingDown} tone="bg-rose-600"/>
          <Stat testid="stat-laba" label="Laba Bersih" value={d?.laba_bersih||0} icon={Wallet} tone="bg-[color:var(--brand)]"/>
          <Stat testid="stat-piutang" label="Piutang Berjalan" value={d?.total_piutang||0} icon={Clock} tone="bg-amber-600"/>
        </div>

        <div className="card-flat p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base md:text-lg font-semibold text-slate-900">Tren 6 Bulan Terakhir</h3>
              <p className="text-xs md:text-sm text-slate-500">Perbandingan pendapatan, pengeluaran, dan laba bersih.</p>
            </div>
          </div>
          <div style={{width: "100%", height: 300}}>
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="name" stroke="#64748B" style={{fontSize: 12}}/>
                <YAxis stroke="#64748B" style={{fontSize: 11}} tickFormatter={(v)=>`${(v/1_000_000).toFixed(0)}jt`}/>
                <Tooltip formatter={(v)=>rupiah(v)} contentStyle={{fontSize: 12, borderRadius: 4, borderColor: "#166db4"}}/>
                <Legend wrapperStyle={{fontSize: 12}}/>
                <Bar dataKey="pemasukan" name="Pemasukan" fill="#166db4" radius={[3,3,0,0]}/>
                <Bar dataKey="pengeluaran" name="Pengeluaran" fill="#EF4444" radius={[3,3,0,0]}/>
                <Line type="monotone" dataKey="laba" name="Laba Bersih" stroke="#10B981" strokeWidth={2} dot={{r:4}}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
}
